/**
 * In-memory log of per-utterance acoustic features and what the gate decided.
 *
 * The point is offline analysis: plot accepted user turns against false triggers
 * in decay-slope / modulation-depth space and see whether they separate. That
 * answers whether proximity cues can replace level-based gating before any of the
 * gating logic is changed.
 *
 * Records start as `pending` and are annotated when the outcome is known, which
 * arrives later over the data channel. Nothing here influences the live session.
 *
 * Recording is always on and always in memory only. Persistence and the export
 * button are opt-in, because records include transcripts and should not be written
 * to a tester's disk unless they asked for it.
 *
 * Field testing on a phone, where there is no console:
 *
 *   open the site with ?voicedebug=1   → survives refresh, adds a "Save log" button
 *   open the site with ?voicedebug=0   → back to memory only, clears the stored log
 *
 * From a desktop console:
 *
 *   window.__voiceFeatures.all()
 *   window.__voiceFeatures.download()
 *   copy(window.__voiceFeatures.toJSON())
 */

const DEBUG_FLAG_KEY = 'voiceFeatureDebug';
const RAW_FLAG_KEY = 'voiceRawAnalysis';
const STORAGE_KEY = 'voiceFeatureLog';
const WINDOW_KEY = '__voiceFeatures';

/** Roughly an hour of conversation. Old records fall off the front. */
export const TELEMETRY_LIMIT = 200;

export const OUTCOMES = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  IGNORED_BY_GATE: 'ignored_by_gate',
  TRANSCRIPT_TIMEOUT: 'transcript_timeout',
  ABANDONED: 'abandoned',
};

/** localStorage throws in some privacy modes and does not exist during prerender. */
const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (key, on) => {
  try {
    if (on) window.localStorage.setItem(key, '1');
    else window.localStorage.removeItem(key);
  } catch {
    /* nothing we can do, and nothing depends on it succeeding */
  }
};

export const debugEnabled = () => readFlag(DEBUG_FLAG_KEY);

/**
 * Lets the flags be set from a URL, which is the only practical way to turn them
 * on for a phone. `?voicedebug=1` persists until explicitly turned off with `=0`.
 */
export function syncVoiceDebugFlags() {
  let params = null;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return { debug: false, raw: false };
  }

  const apply = (param, key) => {
    const value = params.get(param);
    if (value === '1') writeFlag(key, true);
    else if (value === '0') writeFlag(key, false);
  };

  apply('voicedebug', DEBUG_FLAG_KEY);
  apply('voiceraw', RAW_FLAG_KEY);

  if (params.get('voicedebug') === '0') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* already unreachable */
    }
  }

  return { debug: readFlag(DEBUG_FLAG_KEY), raw: readFlag(RAW_FLAG_KEY) };
}

export default class VoiceFeatureTelemetry {
  constructor({ limit = TELEMETRY_LIMIT, storageKey = STORAGE_KEY } = {}) {
    this.limit = limit;
    this.storageKey = storageKey;
    this.records = [];
    this.byId = new Map();
    this.restore();
  }

  /** Picks up records from an earlier page load so a refresh does not lose a session. */
  restore() {
    if (!debugEnabled()) return;
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(stored)) return;
      this.records = stored.slice(-this.limit);
      this.byId = new Map();
      this.records.forEach((entry) => {
        if (entry?.key) this.byId.set(entry.key, entry);
      });
    } catch {
      /* corrupt or unavailable — start clean rather than break voice mode */
    }
  }

  /** Only writes when the tester opted in; transcripts should not land on disk otherwise. */
  persist() {
    if (!debugEnabled()) return;
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.records));
    } catch {
      // Almost certainly the quota. Drop the oldest half and take one more run at it,
      // so a long session degrades to recent history instead of silently stopping.
      this.records = this.records.slice(Math.floor(this.records.length / 2));
      this.byId = new Map();
      this.records.forEach((entry) => {
        if (entry?.key) this.byId.set(entry.key, entry);
      });
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(this.records));
      } catch {
        /* give up on persistence; the in-memory log still works */
      }
    }
  }

  add(record) {
    const entry = { ts: new Date().toISOString(), outcome: OUTCOMES.PENDING, ...record };
    this.records.push(entry);
    if (entry.key) this.byId.set(entry.key, entry);
    while (this.records.length > this.limit) {
      const dropped = this.records.shift();
      if (dropped?.key) this.byId.delete(dropped.key);
    }
    if (debugEnabled()) {
      // eslint-disable-next-line no-console
      console.log('[voice.features]', entry);
    }
    this.persist();
    return entry.key;
  }

  annotate(key, patch) {
    const entry = key ? this.byId.get(key) : null;
    if (!entry) return null;
    Object.assign(entry, patch);
    if (debugEnabled()) {
      // eslint-disable-next-line no-console
      console.log('[voice.features] +', key, patch);
    }
    this.persist();
    return entry;
  }

  /** Marks anything still pending, so an abandoned turn is distinguishable. */
  finalisePending(outcome = OUTCOMES.ABANDONED) {
    let changed = false;
    this.records.forEach((entry) => {
      if (entry.outcome === OUTCOMES.PENDING) {
        Object.assign(entry, { outcome });
        changed = true;
      }
    });
    if (changed) this.persist();
  }

  size() {
    return this.records.length;
  }

  all() {
    return this.records.slice();
  }

  toJSON() {
    return JSON.stringify(this.records, null, 2);
  }

  /** Saves the log as a file — the only way off a phone with no dev tools attached. */
  download(filename) {
    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const blob = new Blob([this.toJSON()], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `voice-features-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return true;
    } catch {
      return false;
    }
  }

  clear() {
    this.records = [];
    this.byId.clear();
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch {
      /* nothing stored */
    }
  }
}

export const voiceFeatureTelemetry = new VoiceFeatureTelemetry();

/** Makes the log reachable from devtools without shipping a UI for it. */
export function exposeTelemetry(instance = voiceFeatureTelemetry) {
  if (typeof window === 'undefined') return;
  window[WINDOW_KEY] = instance;
}
